import React, { useEffect, useMemo, useState } from "react"
import styled from "styled-components"
import { isIE } from "react-device-detect"
import * as lookup from "../../../static/lookup.json"
import { getValueFromJourney } from "../services/helperFunctions"
import {
  validateBiContactConsentPopup,
  CONTACT_CONSENT_ERROR_MESSAGES,
} from "../services/contactConsentPolicy"

const DEFAULT_PRIVACY_HTML =
  '<label for="contactConsentPrivacyPolicy">By submitting my details, I override my NDNC/ NCPR registration and authorize the Company and its representatives including its third party vendors to validate, evaluate and process this proposal and also to contact me via email, phone, WhatsApp or any other mode. I confirm that, I have read and understood the Company\'s <a href="https://www.canarahsbclife.com/privacy-policy.html" class="privacy-policy" target="_blank" rel="noopener noreferrer"> Privacy Policy</a> and agree to abide by the same.\n\n</label>'

const InputContainer = styled.div`
  display: block;
  flex-direction: column;
  margin: 15px 0;
  position: relative;
  box-sizing: border-box;
  font-size: 14px;
  color: #505050;
  float: left;

  & > .str-input {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  & input {
    border: none;
    outline: none;
    margin: 0;
    padding: 0;
    line-height: 4;
    height: 42px;
    font-size: 14px;
    background: ${props =>
      props.channel === "A" ? "#ededed" : "#fff"};
    background: var(--lumiq-bg);
    ${() => {
      if (isIE) return `line-height: 3;`
      return ""
    }}
  }

  & > label {
    margin-left: 8px;
    margin-bottom: 2px;
    color: #505050;
    font-size: 14px;
    background: ${props =>
      props.channel === "A" ? "#ededed" : "#fff"};
    background: var(--lumiq-bg);
    padding: 2px 5px;
    position: relative;
    display: inline-block;
    cursor: default;
    max-width: 100%;
  }
`

const SelectContainer = styled.div`
  display: block;
  margin: 13px 0;
  position: relative;
  box-sizing: border-box;
  font-size: 14px;
  color: #505050;
  float: left;
  min-width: 100%;
  max-width: 100%;

  & > select {
    width: 100%;
    cursor: pointer;
  }

  & > label {
    margin-left: 8px;
    margin-bottom: 2px;
    color: #505050;
    font-size: 14px;
    background: ${props =>
      props.channel === "A" ? "#ededed" : "#fff"};
    background: var(--lumiq-bg);
    padding: 2px 5px;
    z-index: 1;
    position: relative;
    display: inline-block;
    cursor: default;
  }
`

const NumberContainer = styled.div`
  display: block;
  margin: 13px 0;
  position: relative;
  box-sizing: border-box;
  font-size: 14px;
  color: #505050;
  float: left;

  & > label {
    margin-left: 8px;
    margin-bottom: 2px;
    color: #505050;
    font-size: 14px;
    background: ${props =>
      props.channel === "A" ? "#ededed" : "#fff"};
    background: var(--lumiq-bg);
    padding: 2px 5px;
    position: relative;
    display: inline-block;
    cursor: default;
    max-width: 100%;

    & > input {
      background: ${props =>
        props.channel === "A" ? "#ededed" : "#fff"};
      background: var(--lumiq-bg);
    }
  }
`


export default function ContactConsentModal({
  show,
  initialValues = {},
  privacyPolicyHtml = DEFAULT_PRIVACY_HTML,
  channel,
  lookup: journeyLookup,
  config = null,
  onClose,
  onProceed,
}) {
  const [email, setEmail] = useState("")
  const [contactNo, setContactNo] = useState("")
  const [mobileNumber, setMobileNumber] = useState("+91")
  const [contactNoResidence, setContactNoResidence] = useState("")
  const [mobileNumberResidence, setMobileNumberResidence] = useState("+971")
  const [proposerEmail, setProposerEmail] = useState("")
  const [proposerContactNo, setProposerContactNo] = useState("")
  const [proposerMobileNumber, setProposerMobileNumber] = useState("+91")
  const [contactNoproposerResidence, setContactNoproposerResidence] = useState("")
  const [proposerNumberResidence, setProposerNumberResidence] = useState("+971")
  const [privacyPolicy, setPrivacyPolicy] = useState(false)
  const [errors, setErrors] = useState({})
  const [showErrors, setShowErrors] = useState(false)

  const showProposerSection = Boolean(initialValues.requiresProposer)
  const showResidenceFields = Boolean(
    config?.keys?.contactNoResidence && config?.keys?.mobileNumberResidence
  )

  const countryOptions = useMemo(() => {
    const countryCodes = lookup.phoneCountryCodes?.[0] || {}
    return Object.entries(countryCodes).map(([key, val]) => ({
      value: key,
      label: val,
    }))
  }, [])

  const emailLabel =
    getValueFromJourney(journeyLookup?.label, "L0007") || "Email ID"
  const mobileCodeLabel =
    getValueFromJourney(journeyLookup?.label, "L0148") || "Mobile Number"
  const mobileNumberLabel =
    getValueFromJourney(journeyLookup?.label, "L0149") || "Mobile Number"
  const residenceMobileCodeLabel =
    getValueFromJourney(journeyLookup?.label, "L0148") || "Mobile Number"
  const residenceMobileNumberLabel =
    getValueFromJourney(journeyLookup?.label, "L1742") || "Residence Mobile Number"
  const indiaMobileNumberLabel =
    getValueFromJourney(journeyLookup?.label, "L1760") || "Mobile Number"
  const emailPlaceholder =
    getValueFromJourney(journeyLookup?.placeholder, "P0006") ||
    "Enter email ID"
  const proposerEmailLabel =
    getValueFromJourney(journeyLookup?.label, "L0007") || "Proposer Email ID"
  const proposerMobileCodeLabel =
    getValueFromJourney(journeyLookup?.label, "L0148") || "Proposer Mobile Number"
  const proposerMobileNumberLabel =
    getValueFromJourney(journeyLookup?.label, "L0149") || "Proposer Mobile Number"
  const proposerEmailPlaceholder =
    getValueFromJourney(journeyLookup?.placeholder, "P0158") ||
    "Enter proposer email ID"
  const mobileNumberPlaceholder =
    getValueFromJourney(journeyLookup?.placeholder, "P0155") ||
    "Enter mobile number"
  const residenceMobilePlaceholder =
    getValueFromJourney(journeyLookup?.placeholder, "P0240") ||
    "Enter residence mobile number"

  useEffect(() => {
    if (!show) return
    setEmail(initialValues.email || "")
    setContactNo(initialValues.contactNo || "")
    setMobileNumber(initialValues.mobileNumber || "+91")
    setContactNoResidence(initialValues.contactNoResidence || "")
    setMobileNumberResidence(initialValues.mobileNumberResidence || "+971")
    setProposerEmail(initialValues.proposerEmail || "")
    setProposerContactNo(initialValues.proposerContactNo || "")
    setProposerMobileNumber(initialValues.proposerMobileNumber || "+91")
    setContactNoproposerResidence(initialValues.contactNoproposerResidence || "")
    setProposerNumberResidence(initialValues.proposerNumberResidence || "+971")
    setPrivacyPolicy(Boolean(initialValues.privacyPolicy))
    setErrors({})
    setShowErrors(false)
  }, [show, initialValues])
  
  // Clear errors in real-time as user corrects field values
  useEffect(() => {
    if (!showErrors) return
    const validation = validateBiContactConsentPopup({
      email,
      contactNo,
      mobileNumber,
      contactNoResidence,
      mobileNumberResidence,
      privacyPolicy,
      requiresProposer: showProposerSection,
      proposerEmail,
      proposerContactNo,
      proposerMobileNumber,
      contactNoproposerResidence,
      proposerNumberResidence,
    }, config)
    setErrors(validation.errors)
  }, [email, contactNo, mobileNumber, contactNoResidence, mobileNumberResidence, privacyPolicy, proposerEmail, proposerContactNo, proposerMobileNumber, contactNoproposerResidence, proposerNumberResidence, showErrors, showProposerSection])

  if (!show) return null

  const handleProceed = () => {
    const validation = validateBiContactConsentPopup(
      {
        email,
        contactNo,
        mobileNumber,
        contactNoResidence,
        mobileNumberResidence,
        privacyPolicy,
        requiresProposer: showProposerSection,
        proposerEmail,
        proposerContactNo,
        proposerMobileNumber,
        contactNoproposerResidence,
        proposerNumberResidence,
      },
      config
    )

    if (!validation.isValid) {
      setErrors(validation.errors)
      setShowErrors(true)
      return
    }

    onProceed(validation.normalizedValues)
  }

  return (
    <>
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          backgroundColor: "rgba(0,0,0,0.5)",
          zIndex: 999999,
        }}
      />
      <div
        style={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          backgroundColor: "var(--lumiq-bg)",
          padding: "24px",
          border: "3px solid var(--lumiq-card-bg)",
          borderRadius: "8px",
          width: "90%",
          maxWidth: "640px",
          maxHeight: "90vh",
          overflowY: "auto",
          zIndex: 1000000,
        }}
      >
        <span
          onClick={onClose}
          style={{
            position: "absolute",
            top: "8px",
            right: "12px",
            fontSize: "22px",
            fontWeight: "bold",
            color: "var(--lumiq-card-bg)",
            cursor: "pointer",
            lineHeight: "1",
          }}
        >
          &times;
        </span>

        <h4
          style={{
            textAlign: "center",
            color: "var(--lumiq-card-bg)",
            margin: "0 0 20px",
          }}
        >
          Contact Details & Consent
        </h4>

        <div className="section-container">
          <h5
            style={{
              color: "var(--lumiq-card-bg)",
              margin: "0",
              fontSize: "16px",
              fontWeight: 600,
            }}
          >
            Life Assured Details
          </h5>
          <div className="row">
            <div className="field col-12">
              <InputContainer
                channel={channel}
                className="form-group input-container"
              >
                <label
                  htmlFor="contactConsentEmail"
                  className="input-label"
                >
                  {emailLabel}
                </label>
                <div className="form-control str-input">
                  <input
                    id="contactConsentEmail"
                    type="email"
                    style={{ width: "100%" }}
                    placeholder={emailPlaceholder}
                    value={email}
                    onChange={e => setEmail(e.target.value.toLowerCase())}
                    autoComplete="new-password"
                  />
                </div>
                {showErrors && errors.email && (
                  <span className="input-error2">{errors.email}</span>
                )}
              </InputContainer>
            </div>
          </div>

          {showResidenceFields ? (
            <>
              <div className="row">
                <div className="field col-4">
                  <SelectContainer
                    channel={channel}
                    className="input-container"
                  >
                    <label
                      htmlFor="contactConsentResidenceMobileCode"
                      className="input-label"
                    >
                      {residenceMobileCodeLabel}
                    </label>
                    <select
                      id="contactConsentResidenceMobileCode"
                      name="Residence Mobile Number"
                      className={`form-control ${isIE ? "IELineHeight" : ""}`}

                      value={mobileNumberResidence}
                      onChange={e => setMobileNumberResidence(e.target.value || "+971")}
                    >
                      {countryOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </SelectContainer>
                </div>

                <div className="field col-8">
                  <NumberContainer
                    channel={channel}
                    className="form-group input-container"
                  >
                    <label
                      htmlFor="contactConsentResidenceMobile"
                      className="input-label"
                    >
                      {residenceMobileNumberLabel}
                    </label>
                    <input
                      id="contactConsentResidenceMobile"
                      type="text"
                      className={`form-control ${isIE ? "IELineHeight" : ""}`}
                      placeholder={residenceMobilePlaceholder}
                      value={contactNoResidence}
                      maxLength={mobileNumberResidence === "+91" ? 10 : 15}
                      onChange={e => {
                        const next = e.target.value.replace(/\D/g, "")
                        const maxLen = mobileNumberResidence === "+91" ? 10 : 15
                        if (next.length <= maxLen) setContactNoResidence(next)
                      }}
                      autoComplete="new-password"
                    />
                    {showErrors && errors.contactNoResidence && (
                      <span className="input-error2">
                        {errors.contactNoResidence}
                      </span>
                    )}
                  </NumberContainer>
                </div>
              </div>

              <div className="row">
                <div className="field col-4">
                  <SelectContainer
                    channel={channel}
                    className="input-container"
                  >
                    <label htmlFor="contactConsentMobileCode" className="input-label">
                      {mobileCodeLabel}
                    </label>
                    <select
                      id="contactConsentMobileCode"
                      name="Mobile Number"
                      className={`form-control ${isIE ? "IELineHeight" : ""}`}

                      value={mobileNumber}
                      onChange={e => setMobileNumber(e.target.value || "+91")}
                    >
                      {countryOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </SelectContainer>
                </div>

                <div className="field col-8">
                  <NumberContainer
                    channel={channel}
                    className="form-group input-container"
                  >
                    <label htmlFor="contactConsentMobile" className="input-label">
                      {indiaMobileNumberLabel}
                    </label>
                    <input
                      id="contactConsentMobile"
                      type="text"
                      className={`form-control ${isIE ? "IELineHeight" : ""}`}
                      placeholder={mobileNumberPlaceholder}
                      value={contactNo}
                      maxLength={mobileNumber === "+91" ? 10 : 15}
                      onChange={e => {
                        const next = e.target.value.replace(/\D/g, "")
                        const maxLen = mobileNumber === "+91" ? 10 : 15
                        if (next.length <= maxLen) setContactNo(next)
                      }}
                      autoComplete="new-password"
                    />
                    {showErrors && errors.contactNo && (
                      <span className="input-error2">{errors.contactNo}</span>
                    )}
                  </NumberContainer>
                </div>
              </div>
            </>
          ) : (
            <div className="row">
              <div className="field col-4">
                <SelectContainer
                  channel={channel}
                  className="input-container"
                >
                  <label htmlFor="contactConsentMobileCode" className="input-label">
                    {mobileCodeLabel}
                  </label>
                  <select
                      id="contactConsentMobileCode"
                      name="Mobile Number"
                      className={`form-control ${isIE ? "IELineHeight" : ""}`}

                      value={mobileNumber}
                      onChange={e => setMobileNumber(e.target.value || "+91")}
                    >
                      {countryOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                </SelectContainer>
              </div>

              <div className="field col-8">
                <NumberContainer
                  channel={channel}
                  className="form-group input-container"
                >
                  <label htmlFor="contactConsentMobile" className="input-label">
                    {mobileNumberLabel}
                  </label>
                  <input
                    id="contactConsentMobile"
                    type="text"
                    className={`form-control ${isIE ? "IELineHeight" : ""}`}
                    placeholder={mobileNumberPlaceholder}
                    value={contactNo}
                    maxLength={mobileNumber === "+91" ? 10 : 15}
                    onChange={e => {
                      const next = e.target.value.replace(/\D/g, "")
                      const maxLen = mobileNumber === "+91" ? 10 : 15
                      if (next.length <= maxLen) setContactNo(next)
                    }}
                    autoComplete="new-password"
                  />
                  {showErrors && errors.contactNo && (
                    <span className="input-error2">{errors.contactNo}</span>
                  )}
                </NumberContainer>
              </div>
            </div>
          )}

          {showProposerSection && (
            <>
              <h5
                style={{
                  color: "var(--lumiq-card-bg)",
                  margin: "20px 0 0px",
                  fontSize: "16px",
                  fontWeight: 600,
                  clear: "both",
                }}
              >
                Proposer Details
              </h5>
              <div className="row">
                <div className="field col-12">
                  <InputContainer
                    channel={channel}
                    className="form-group input-container"
                  >
                    <label
                      htmlFor="contactConsentProposerEmail"
                      className="input-label"
                    >
                      {proposerEmailLabel}
                    </label>
                    <div className="form-control str-input">
                      <input
                        id="contactConsentProposerEmail"
                        type="email"
                        style={{ width: "100%" }}
                        placeholder={proposerEmailPlaceholder}
                        value={proposerEmail}
                        onChange={e =>
                          setProposerEmail(e.target.value.toLowerCase())
                        }
                        autoComplete="new-password"
                      />
                    </div>
                    {showErrors && errors.proposerEmail && (
                      <span className="input-error2">{errors.proposerEmail}</span>
                    )}
                  </InputContainer>
                </div>
              </div>

              {showResidenceFields && (
                <div className="row">
                  <div className="field col-4">
                    <SelectContainer
                      channel={channel}
                      className="input-container"
                    >
                      <label
                        htmlFor="contactConsentProposerResidenceMobileCode"
                        className="input-label"
                      >
                        {proposerMobileCodeLabel}
                      </label>
                      <select
                      id="contactConsentProposerResidenceMobileCode"
                      name="Proposer Residence Mobile Number"
                      className={`form-control ${isIE ? "IELineHeight" : ""}`}

                      value={proposerNumberResidence}
                      onChange={e => setProposerNumberResidence(e.target.value || "+971")}
                    >
                      {countryOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    </SelectContainer>
                  </div>

                  <div className="field col-8">
                    <NumberContainer
                      channel={channel}
                      className="form-group input-container"
                    >
                      <label
                        htmlFor="contactConsentProposerResidenceMobile"
                        className="input-label"
                      >
                        {residenceMobileNumberLabel}
                      </label>
                      <input
                        id="contactConsentProposerResidenceMobile"
                        type="text"
                        className={`form-control ${isIE ? "IELineHeight" : ""}`}
                        placeholder={residenceMobilePlaceholder}
                        value={contactNoproposerResidence}
                        maxLength={proposerNumberResidence === "+91" ? 10 : 15}
                        onChange={e => {
                          const next = e.target.value.replace(/\D/g, "")
                          const maxLen = proposerNumberResidence === "+91" ? 10 : 15
                          if (next.length <= maxLen) {
                            setContactNoproposerResidence(next)
                          }
                        }}
                        autoComplete="new-password"
                      />
                      {showErrors && errors.contactNoproposerResidence && (
                        <span className="input-error2">
                          {errors.contactNoproposerResidence}
                        </span>
                      )}
                    </NumberContainer>
                  </div>
                </div>
              )}

              <div className="row">
                <div className="field col-4">
                  <SelectContainer
                    channel={channel}
                    className="input-container"
                  >
                    <label
                      htmlFor="contactConsentProposerMobileCode"
                      className="input-label"
                    >
                      {proposerMobileCodeLabel}
                    </label>
                    <select
                      id="contactConsentProposerMobileCode"
                      name="Proposer Mobile Number"
                      className={`form-control ${isIE ? "IELineHeight" : ""}`}

                      style={{lineHeight:1.5}}
                      value={proposerMobileNumber}
                      onChange={e => setProposerMobileNumber(e.target.value || "+91")}
                    >
                      {countryOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </SelectContainer>
                </div>

                <div className="field col-8">
                  <NumberContainer
                    channel={channel}
                    className="form-group input-container"
                  >
                    <label
                      htmlFor="contactConsentProposerMobile"
                      className="input-label"
                    >
                      {showResidenceFields
                        ? indiaMobileNumberLabel
                        : proposerMobileNumberLabel}
                    </label>
                    <input
                      id="contactConsentProposerMobile"
                      type="text"
                      className={`form-control ${isIE ? "IELineHeight" : ""}`}
                      placeholder={mobileNumberPlaceholder}
                      value={proposerContactNo}
                      maxLength={proposerMobileNumber === "+91" ? 10 : 15}
                      onChange={e => {
                        const next = e.target.value.replace(/\D/g, "")
                        const maxLen = proposerMobileNumber === "+91" ? 10 : 15
                        if (next.length <= maxLen) setProposerContactNo(next)
                      }}
                      autoComplete="new-password"
                    />
                    {showErrors && errors.proposerContactNo && (
                      <span className="input-error2">
                        {errors.proposerContactNo}
                      </span>
                    )}
                  </NumberContainer>
                </div>
              </div>
            </>
          )}

          <div className="row">
            <div className="field col-md-12 col-xs-12">
              <div style={{ overflow: "hidden", width: "100%" }}>
                <input
                  id="contactConsentPrivacyPolicy"
                  type="checkbox"
                  style={{
                    width: "25px",
                    height: "25px",
                    display: "block",
                    float: "left",
                  }}
                  checked={privacyPolicy}
                  onChange={e => setPrivacyPolicy(e.target.checked)}
                />
                <div
                  dangerouslySetInnerHTML={{ __html: privacyPolicyHtml }}
                  style={{
                    display: "block",
                    float: "right",
                    width: "calc(100% - 50px)",
                  }}
                />
              </div>
              {showErrors && errors.privacyPolicy && (
                <span className="input-error2" style={{ marginLeft: "50px" }}>
                  {errors.privacyPolicy ||
                    CONTACT_CONSENT_ERROR_MESSAGES.consentRequired}
                </span>
              )}
            </div>
          </div>
        </div>

        <button
          type="button"
          onClick={handleProceed}
          style={{
            backgroundColor: "var(--lumiq-tertiary)",
            color: "#fff",
            padding: "10px 24px",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
            display: "block",
            margin: "0 auto",
          }}
        >
          Proceed
        </button>
      </div>
    </>
  )
}
